import React, { useMemo, useState } from 'react';

// PenumbraChart — v2.2.3 Radial Coxcomb.
//
// The inner ring ("The Math") slices by Price, dispassionate and
// solid: each slice angle = priceShare × 360°, bounded by two fixed
// radii. The outer ring ("The Penumbra / Vibes") shares the SAME
// angular slices as the inner ring — one outcome occupies the same
// arc in both rings — but its OUTER RADIUS varies per slice with
// vibe share. This is a coxcomb / Nightingale rose mapping:
//
//   innerRing_InnerRadius  = R_INNER_IN   (constant)
//   innerRing_OuterRadius  = R_INNER_OUT  (constant)
//   penumbraRing_InnerRadius = R_OUTER_IN (constant)
//   penumbraRing_OuterRadius = R_OUTER_IN + PENUMBRA_BASELINE
//                            + vibeShare × (PENUMBRA_MAX − PENUMBRA_BASELINE)
//
// Consequences by design:
//   * Inner and outer slices line up perfectly per outcome — no
//     angular mismatch between "the math" and "the vibes".
//   * An outcome with 10 % probability but 90 % of the vibes is a
//     narrow slice that spikes violently outward.
//   * An outcome with zero vibes still renders a 1-unit baseline
//     sliver so the Penumbra remains a visible ring rather than
//     disappearing gaps.
//   * Full-circle edge case (single-outcome market) is stitched from
//     two 180° arcs — SVG's `A` can't draw start === end in one go.
//
// Geometry is fixed to a 100×100 viewBox so the radii below read as
// percentages and scale invariantly with the `size` prop.

export interface PenumbraOutcome {
  id:     string;
  label:  string;
  color:  string;
  price:  number;  // 0..1
  vibe:   number;  // raw vibe score 0..1 per outcome (normalised here)
  // v2.3 — outer ring slices pulse continuously when an outcome has
  // any vibes, and the tempo + glow intensity scale with vibe share.
  // The old transient `pulse?: boolean` (price-flash) is retired: the
  // Penumbra is the *sentiment* ring, so its heartbeat must be driven
  // by vibe traffic, not price deltas.
}

export type HoverPayload =
  | { id: string; ring: 'INNER' | 'OUTER' }
  | null;

interface Props {
  outcomes: PenumbraOutcome[];
  size?:    number;
  onHover?: (h: HoverPayload) => void;
  centerTop?:    string;
  centerBottom?: string;
  // Single-ring mode (2026-05-16): suppress the Penumbra outer ring,
  // the white Border-of-Reality delineator, and the pulse keyframes
  // entirely. The inner price-share doughnut + hover + centre text
  // still render. Used on Arena (IS_STRIPPED_DOWN) where the vibe
  // surface is held back but the price doughnut continues to render
  // as the cockpit's centre visual.
  noPenumbra?: boolean;
  // I-065 settled-state highlight (DECISIONS.md 2026-05-17). When
  // set to a non-null label that matches one of the outcomes' .label
  // values, the matched slice stays at full opacity while non-winning
  // slices drop to 0.5; a small ✓ glyph is rendered at the centroid
  // of the winning slice's inner ring. Prices are NOT recomputed as
  // winning-outcome-as-100% — the doughnut preserves the final
  // implied-probability distribution (what the market said) so the
  // divergence-as-instrument signal is visible alongside the result.
  winningOutcomeLabel?: string | null;
}

// ── Fixed coordinate space ───────────────────────────────────────────
const VB  = 100;
const CX  = 50;
const CY  = 50;

// ── Inner ring: fixed band ───────────────────────────────────────────
// v2.2.8 "Hard Floor" — the inner ring's outer edge is pinned to the
// white delineator's radius. No void below the white ring. The white
// circle reads as the *surface* of the priced truth, and the outer
// Penumbra slices read as solar flares floating above it.
const R_INNER_IN  = 22;
const R_INNER_OUT = 33;     // = R_BORDER → zero gap.

// ── Border of Reality (between the rings) ────────────────────────────
// Crisp, thin, bright white — the holographic delineator. Drawn as a
// standalone <circle> (no fill). Hard-floors the inner ring below,
// floats a 3-unit void above before the Penumbra slices begin.
//
//   inner-ring outer edge:  R_INNER_OUT = 33   (flush with white)
//   white stroke centred at R_BORDER   = 33
//   void:                   33.3 → 36   (~2.7u)
//   outer-ring inner edge:  R_OUTER_IN = 36
const R_BORDER     = 33;
const BORDER_W     = 0.6;
const BORDER_COLOR = '#FFFFFF';
const BORDER_ALPHA = 1.0;

// Slice delineator colour. Matches the modal panel background so the
// stroke between adjacent slices reads as a "negative-space" cut-out
// rather than a grey outline. The outer-edge stroke is the same
// colour, which makes it visually disappear into the background — the
// outer slices appear to float freely. Keep this in sync with the
// MarketFocusModal panel `background`.
const SLICE_GAP_COLOR = '#0a121d';
const SLICE_GAP_W     = 0.5;

// ── Outer (Penumbra) ring: variable outer edge per slice ─────────────
// v2.2.7 spec:
//   OuterRing_OuterRadius = OuterRing_InnerRadius + (VibeShare × 30)
// No baseline — a zero-vibe outcome collapses to zero thickness, by
// design. SVG has `overflow: visible` so the full spike can render
// beyond the nominal 100×100 viewBox when vibeShare approaches 1.
const R_OUTER_IN     = 36;
const PENUMBRA_SPIKE = 30;

function penumbraOuterRadius(vibeShare: number): number {
  const t = vibeShare > 0 ? Math.min(1, vibeShare) : 0;
  return R_OUTER_IN + t * PENUMBRA_SPIKE;
}

// Polar → cartesian for SVG. Y grows downward, so we subtract on Y.
// 0° = top, angles increase clockwise.
function polar(r: number, angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180;
  return [CX + r * Math.sin(rad), CY - r * Math.cos(rad)];
}

function fmt(n: number): string {
  return n.toFixed(4);
}

// SVG path for a donut slice between two radii and two angles.
// A full 360° slice is split into two 180° arcs because SVG's arc
// command cannot draw a complete circle when the start and end
// points coincide.
function slicePath(
  rInner: number, rOuter: number,
  startDeg: number, endDeg: number,
): string {
  const span = endDeg - startDeg;
  if (span <= 0 || rOuter <= rInner) return '';
  if (span >= 359.999) {
    const [oax, oay] = polar(rOuter,   0);
    const [obx, oby] = polar(rOuter, 180);
    const [iax, iay] = polar(rInner,   0);
    const [ibx, iby] = polar(rInner, 180);
    return [
      `M ${fmt(oax)} ${fmt(oay)}`,
      `A ${rOuter} ${rOuter} 0 0 1 ${fmt(obx)} ${fmt(oby)}`,
      `A ${rOuter} ${rOuter} 0 0 1 ${fmt(oax)} ${fmt(oay)}`,
      `M ${fmt(iax)} ${fmt(iay)}`,
      `A ${rInner} ${rInner} 0 0 0 ${fmt(ibx)} ${fmt(iby)}`,
      `A ${rInner} ${rInner} 0 0 0 ${fmt(iax)} ${fmt(iay)}`,
      'Z',
    ].join(' ');
  }
  const largeArc = span > 180 ? 1 : 0;
  const [x1, y1] = polar(rOuter, startDeg);
  const [x2, y2] = polar(rOuter, endDeg);
  const [x3, y3] = polar(rInner, endDeg);
  const [x4, y4] = polar(rInner, startDeg);
  return [
    `M ${fmt(x1)} ${fmt(y1)}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${fmt(x2)} ${fmt(y2)}`,
    `L ${fmt(x3)} ${fmt(y3)}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${fmt(x4)} ${fmt(y4)}`,
    'Z',
  ].join(' ');
}

function safe(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export default function PenumbraChart({
  outcomes, size = 320, onHover, centerTop, centerBottom,
  noPenumbra = false, winningOutcomeLabel = null,
}: Props): React.JSX.Element {
  const [hover, setHover] = useState<HoverPayload>(null);
  const hasWinner = !!winningOutcomeLabel;

  // One slice descriptor per outcome. Inner and outer rings share the
  // same start/end angles (driven by price share). Only the outer
  // radius varies, driven by vibe share — that's the coxcomb spike.
  const slices = useMemo(() => {
    if (outcomes.length === 0) return [] as Array<{
      outcome: PenumbraOutcome;
      priceShare: number;
      vibeShare: number;
      startDeg: number;
      endDeg: number;
    }>;
    const totalPrice = outcomes.reduce((s, o) => s + safe(o.price), 0) || 1;
    const totalVibe  = outcomes.reduce((s, o) => s + safe(o.vibe),  0) || 1;
    let cum = 0;
    return outcomes.map(o => {
      const priceShare = safe(o.price) / totalPrice;
      const vibeShare  = safe(o.vibe)  / totalVibe;
      const startDeg   = cum * 360;
      cum             += priceShare;
      const endDeg     = cum * 360;
      return { outcome: o, priceShare, vibeShare, startDeg, endDeg };
    });
  }, [outcomes]);

  function setH(next: HoverPayload): void {
    setHover(next);
    onHover?.(next);
  }

  if (outcomes.length === 0) {
    return (
      <div style={{
        width: size, height: size,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#334155', fontFamily: 'monospace', fontSize: 11,
      }}>
        No outcomes
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      {/* v2.3 Holographic pulse — opacity + a coloured glow whose
          tempo and blur radius scale with each slice's vibe share.
          `currentColor` is set per-slice so each pulse glows in the
          outcome's own colour. Custom properties let us animate the
          blur radius per-slice without a per-slice keyframe:
            --ppg-lo · low point of the drop-shadow blur
            --ppg-hi · high point of the drop-shadow blur
            --ppo-lo · low point of opacity
            --ppo-hi · high point of opacity
          Duration is set inline via `animation-duration` on each path
          — high-vibe slices beat faster (≈0.8s), quiet slices amble
          (≈2.4s). */}
      {!noPenumbra && (
        <style>{`
          @keyframes penumbra-pulse {
            0%, 100% {
              opacity: var(--ppo-lo, 0.78);
              filter:  drop-shadow(0 0 var(--ppg-lo, 1.5px) currentColor);
            }
            50% {
              opacity: var(--ppo-hi, 1.00);
              filter:  drop-shadow(0 0 var(--ppg-hi, 4px) currentColor);
            }
          }
          .penumbra-outer-pulse {
            animation-name: penumbra-pulse;
            animation-timing-function: ease-in-out;
            animation-iteration-count: infinite;
          }
        `}</style>
      )}

      <svg
        width={size} height={size}
        viewBox={`0 0 ${VB} ${VB}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label="Market probability versus community vibe"
      >
        {/* Outer ring (Penumbra / Vibes) — same angular slices as the
            inner ring; thickness varies with vibe share. Drawn first
            so the inner ring and Border of Reality sit visually on
            top of any halo rim. Suppressed entirely in noPenumbra
            (single-ring) mode. */}
        {!noPenumbra && slices.map(s => {
          if (s.priceShare <= 0) return null;
          const rOuter = penumbraOuterRadius(s.vibeShare);
          const d = slicePath(R_OUTER_IN, rOuter, s.startDeg, s.endDeg);
          if (!d) return null;
          const active = hover?.ring === 'OUTER' && hover.id === s.outcome.id;
          const dimmed = hover !== null && !active;
          // v2.3 vibe pulse — any slice with a non-zero vibe share
          // pulses; tempo (0.8s fast → 2.4s slow) and glow radius
          // (1.5px → 6px) scale with vibeShare. The result: high-
          // traffic outcomes read as urgent heartbeats, low-traffic
          // outcomes amble. `intensity` clamps to [0,1] so a single-
          // outcome market (vibeShare=1) hits max tempo cleanly.
          const pulses = s.vibeShare > 0;
          const intensity = Math.min(1, Math.max(0, s.vibeShare));
          const pulseDur  = 2.4 - intensity * 1.6;  // 0.8s at full, 2.4s at floor
          const glowLo    = 1.5 + intensity * 1.5;  // 1.5 → 3.0 px
          const glowHi    = 4   + intensity * 4;    // 4   → 8   px
          const opHi      = 1.00;
          const opLo      = 0.78 + intensity * 0.10; // quieter slices swing more
          // React's CSSProperties doesn't know about custom props;
          // cast-through object keeps `--ppg-lo` etc. typed loosely.
          const pulseStyle: React.CSSProperties = pulses
            ? ({
                animationDuration: `${pulseDur.toFixed(2)}s`,
                ['--ppg-lo' as any]: `${glowLo.toFixed(2)}px`,
                ['--ppg-hi' as any]: `${glowHi.toFixed(2)}px`,
                ['--ppo-lo' as any]: opLo.toFixed(2),
                ['--ppo-hi' as any]: opHi.toFixed(2),
              } as React.CSSProperties)
            : {};
          // I-065: also dim non-winning slices on the outer ring when
          // the market is settled (parallel to the inner ring rule).
          // Only relevant when noPenumbra=false (i.e. Live / non-Arena
          // contexts where Penumbra is still drawn).
          const isLoserOuter = hasWinner && s.outcome.label !== winningOutcomeLabel;
          const baseOpacity  = isLoserOuter ? 0.35 : 0.78;
          return (
            <path
              key={`outer-${s.outcome.id}`}
              d={d}
              fill={s.outcome.color}
              stroke={SLICE_GAP_COLOR}
              strokeWidth={SLICE_GAP_W}
              strokeLinejoin="miter"
              className={pulses && !hasWinner ? 'penumbra-outer-pulse' : undefined}
              style={{
                color:      s.outcome.color,
                opacity:    dimmed ? 0.35 : baseOpacity,
                cursor:     'pointer',
                transition: 'opacity 0.15s ease',
                ...(hasWinner ? {} : pulseStyle),
              }}
              onMouseEnter={() => setH({ id: s.outcome.id, ring: 'OUTER' })}
              onMouseLeave={() => setH(null)}
            />
          );
        })}

        {/* Inner ring (The Math) — priced. Solid, dispassionate.
            v2.2.8: drawn BEFORE the Border-of-Reality so the white
            ring sits flush on top of the inner slices' outer edge —
            the "hard floor" look. I-065: when winningOutcomeLabel is
            set, non-winning slices dim to 0.5; hover still wins so
            invitees can interrogate any slice's price. */}
        {slices.map(s => {
          if (s.priceShare <= 0) return null;
          const d = slicePath(R_INNER_IN, R_INNER_OUT, s.startDeg, s.endDeg);
          if (!d) return null;
          const active   = hover?.ring === 'INNER' && hover.id === s.outcome.id;
          const dimmed   = hover !== null && !active;
          const isLoser  = hasWinner && s.outcome.label !== winningOutcomeLabel;
          const opacity  = active ? 1 : dimmed ? 0.55 : isLoser ? 0.5 : 1;
          return (
            <path
              key={`inner-${s.outcome.id}`}
              d={d}
              fill={s.outcome.color}
              stroke={SLICE_GAP_COLOR}
              strokeWidth={SLICE_GAP_W}
              strokeLinejoin="miter"
              style={{
                opacity,
                cursor:     'pointer',
                transition: 'opacity 0.15s ease',
              }}
              onMouseEnter={() => setH({ id: s.outcome.id, ring: 'INNER' })}
              onMouseLeave={() => setH(null)}
            />
          );
        })}

        {/* I-065 winner glyph — ✓ at the centroid of the winning inner
            slice. Rendered after the slice paths so it sits on top;
            non-interactive so it doesn't steal the hover.
            Skipped if the winning outcome's slice is too narrow to
            host the glyph readably (priceShare < 5%). */}
        {hasWinner && (() => {
          const winner = slices.find(s => s.outcome.label === winningOutcomeLabel);
          if (!winner || winner.priceShare < 0.05) return null;
          const midDeg = (winner.startDeg + winner.endDeg) / 2;
          const r      = (R_INNER_IN + R_INNER_OUT) / 2;
          const [gx, gy] = polar(r, midDeg);
          return (
            <text
              x={gx}
              y={gy}
              fill="#ffffff"
              fontSize={6}
              fontWeight={800}
              textAnchor="middle"
              dominantBaseline="central"
              style={{ pointerEvents: 'none', filter: 'drop-shadow(0 0 0.6px rgba(0,0,0,0.8))' }}
            >
              ✓
            </text>
          );
        })()}

        {/* Border-of-Reality — standalone <circle>, no fill. Pinned at
            R_BORDER regardless of vibe. Drawn LAST so it sits visually
            on top of the inner ring's outer edge. Crisp bright white —
            the Norwich holographic look. Suppressed in noPenumbra mode
            so the cockpit reads as a single clean ring (no decorative
            outline marking the seam between rings that no longer exist
            together). */}
        {!noPenumbra && (
          <circle
            cx={CX} cy={CY}
            r={R_BORDER}
            fill="none"
            stroke={BORDER_COLOR}
            strokeWidth={BORDER_W}
            opacity={BORDER_ALPHA}
          />
        )}
      </svg>

      {/* Center text overlay. Parent owns the default copy via
          centerTop/centerBottom; we swap in the hovered outcome's
          label + metric + ring tag on hover. */}
      <div style={{
        position:      'absolute',
        inset:         0,
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        justifyContent:'center',
        pointerEvents: 'none',
        textAlign:     'center',
        fontFamily:    'monospace',
      }}>
        {hover ? (
          (() => {
            const match = slices.find(s => s.outcome.id === hover.id);
            if (!match) return null;
            // INNER shows the price (implied probability). OUTER shows
            // the normalised vibe share so the coxcomb thickness has a
            // numeric readout.
            const readout = hover.ring === 'INNER'
              ? `${(match.outcome.price * 100).toFixed(1)}%`
              : `${(match.vibeShare     * 100).toFixed(1)}%`;
            const tag = hover.ring === 'INNER' ? 'PRICE · IMPLIED' : 'VIBE · SENTIMENT';
            return (
              <>
                <div style={{
                  color:         match.outcome.color,
                  fontSize:      11,
                  letterSpacing: '0.22em',
                  fontWeight:    800,
                  textTransform: 'uppercase',
                  maxWidth:      '80%',
                  lineHeight:    1.2,
                }}>
                  {match.outcome.label}
                </div>
                <div style={{
                  color:         '#e2e8f0',
                  fontSize:      22,
                  fontWeight:    800,
                  marginTop:     4,
                  letterSpacing: '0.04em',
                }}>
                  {readout}
                </div>
                <div style={{
                  color:         '#64748b',
                  fontSize:      8,
                  letterSpacing: '0.24em',
                  marginTop:     2,
                }}>
                  {tag}
                </div>
              </>
            );
          })()
        ) : (
          <>
            {centerTop && (
              <div style={{
                color:         '#e2e8f0',
                fontSize:      11,
                letterSpacing: '0.22em',
                fontWeight:    800,
                textTransform: 'uppercase',
                maxWidth:      '80%',
                lineHeight:    1.2,
              }}>
                {centerTop}
              </div>
            )}
            {centerBottom && (
              <div style={{
                color:         '#64748b',
                fontSize:      9,
                letterSpacing: '0.22em',
                marginTop:     6,
              }}>
                {centerBottom}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
